<template>
  <div class="import-job-list-panel">
    <p v-if="scopeHint" class="is-size-7 has-text-grey mb-2">{{ scopeHint }}</p>
    <p v-if="hint" class="is-size-7 has-text-grey mb-3">{{ hint }}</p>
    <div class="columns is-multiline is-align-items-end mb-4 import-job-list-filters">
      <div class="column is-narrow">
        <b-field label="Status" class="mb-0">
          <b-select v-model="statusFilter" size="is-small">
            <option v-for="o in statusOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-narrow">
        <b-field label="Created (date range)" class="mb-0">
          <b-datepicker
            v-model="dateRange"
            size="is-small"
            range
            placeholder="Any"
            icon="calendar-alt"
            trap-focus
            :icon-right="dateRange ? 'close-circle' : ''"
            icon-right-clickable
            @icon-right-click="dateRange = null"
          />
        </b-field>
      </div>
      <div class="column is-narrow import-job-list-filters__col-perpage">
        <b-field label="Per page" class="mb-0">
          <b-select v-model="perPage" size="is-small" @input="onPerPageChange">
            <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-narrow">
        <b-button size="is-small" type="is-light" icon-left="sync" :loading="loading" @click="load">Refresh</b-button>
      </div>
    </div>
    <div v-if="loading" class="has-text-centered py-5">Loading…</div>
    <div v-else class="import-job-list-scroll">
      <p v-if="!pagedJobs.length" class="has-text-grey has-text-centered py-5">No import jobs match your filters.</p>
      <catalog-item-import-job-card v-for="j in pagedJobs" :key="j.id" :job="j" class="mb-2" />
    </div>
    <b-pagination
      v-if="filteredJobs.length > perPage"
      v-model="page"
      class="mt-3 is-justify-content-center"
      :total="filteredJobs.length"
      :per-page="perPage"
      order="is-centered"
      size="is-small"
      icon-prev="chevron-left"
      icon-next="chevron-right"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs';

import {
  canViewAllCatalogImportJobs,
  fetchMyImportJobsForList,
  IMPORT_JOB_LIST_LIMIT,
} from '@/service/catalogItemImportJob.service';

import CatalogItemImportJobCard from './CatalogItemImportJobCard.vue';

function jobTimeMs(j) {
  const c = j.createdAt;
  if (c == null) return null;
  if (typeof c.toDate === 'function') return c.toDate().getTime();
  if (c.seconds != null) return c.seconds * 1000;
  return null;
}

export default {
  name: 'CatalogItemImportJobListPanel',
  components: { CatalogItemImportJobCard },
  props: {
    prefetched: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      rawJobs: [],
      statusFilter: 'all',
      dateRange: null,
      page: 1,
      perPage: 10,
      perPageOptions: [10, 20, 50, 100],
      statusOptions: [
        { value: 'all', label: 'All statuses' },
        { value: 'queued', label: 'Queued' },
        { value: 'processing', label: 'Processing' },
        { value: 'completed', label: 'Completed' },
        { value: 'failed', label: 'Failed' },
      ],
    };
  },
  computed: {
    scopeHint() {
      if (canViewAllCatalogImportJobs()) {
        return "All users' import jobs.";
      }
      return 'Only jobs you created are listed.';
    },
    hint() {
      return this.rawJobs.length >= IMPORT_JOB_LIST_LIMIT
        ? `Showing the ${IMPORT_JOB_LIST_LIMIT} most recent jobs.`
        : '';
    },
    filteredJobs() {
      let list = this.rawJobs;
      const sf = this.statusFilter;
      if (sf !== 'all') {
        const w = String(sf).toLowerCase();
        list = list.filter(j => String(j.status || '').toLowerCase() === w);
      }
      const dr = this.dateRange;
      if (dr && dr.length === 2 && dr[0] && dr[1]) {
        const a = dayjs(dr[0]).startOf('day').valueOf();
        const b = dayjs(dr[1]).endOf('day').valueOf();
        list = list.filter(j => {
          const t = jobTimeMs(j);
          if (t == null) return false;
          return t >= a && t <= b;
        });
      }
      return list;
    },
    pagedJobs() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredJobs.slice(start, start + this.perPage);
    },
  },
  watch: {
    prefetched(n) {
      if (n == null) return;
      this.rawJobs = n.slice();
    },
    statusFilter() {
      this.page = 1;
    },
    dateRange() {
      this.page = 1;
    },
    filteredJobs(n) {
      const maxP = Math.max(1, Math.ceil(n.length / this.perPage) || 1);
      if (this.page > maxP) {
        this.page = maxP;
      }
    },
  },
  mounted() {
    if (this.prefetched != null) {
      this.rawJobs = this.prefetched.slice();
      this.$emit('list-synced', this.rawJobs);
      return;
    }
    this.load();
  },
  methods: {
    onPerPageChange() {
      this.page = 1;
    },
    async load() {
      this.loading = true;
      try {
        this.rawJobs = await fetchMyImportJobsForList();
        this.$emit('list-synced', this.rawJobs);
        this.page = 1;
      } catch (e) {
        this.$buefy.toast.open({ message: (e && e.message) || 'Could not load jobs', type: 'is-danger' });
        this.rawJobs = [];
        this.$emit('list-synced', this.rawJobs);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.import-job-list-scroll {
  max-height: min(52vh, 36rem);
  overflow-y: auto;
  padding-right: 0.25rem;
}
.import-job-list-filters {
  gap: 0.5rem 0;
}
.import-job-list-filters__col-perpage {
  min-width: 9.25rem;
}
.import-job-list-filters__col-perpage :deep(.field.is-floating-in-label > .label) {
  max-width: none;
  overflow: visible;
  text-overflow: clip;
}
@media screen and (max-width: 768px) {
  .import-job-list-scroll {
    max-height: min(45vh, 28rem);
  }
  .import-job-list-filters .column {
    flex: none;
    width: 100%;
  }
  .import-job-list-filters__col-perpage {
    min-width: 0;
  }
}
</style>
